import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
// The shared app i18n instance (German/English, stored in localStorage).
import { i18n, setLocale } from '@/i18n';

export type DataPortalLocale = 'de' | 'en';

/**
 * Data portal messages live in `locales/<lang>/<area>.json` and are merged
 * into the app i18n instance under `dp.<area>`, e.g. `dp.home.title`.
 */
const files = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  eager: true,
  import: 'default',
});
const messages: Record<string, Record<string, unknown>> = {};
for (const [path, content] of Object.entries(files)) {
  const [, locale, area] =
    /\.\/locales\/(\w+)\/([\w-]+)\.json$/.exec(path) ?? [];
  if (!locale || !area) continue;
  (messages[locale] ??= {})[area] = content;
}
for (const [locale, areas] of Object.entries(messages))
  i18n.global.mergeLocaleMessage(locale, { dp: areas });

export function dataPortalLocale(): DataPortalLocale {
  return i18n.global.locale.value === 'en' ? 'en' : 'de';
}

/** BCP 47 tag for Intl date and number formatting in the current language. */
export function dataPortalLocaleTag(): string {
  return dataPortalLocale() === 'en' ? 'en-GB' : 'de-DE';
}

export function setDataPortalLocale(locale: DataPortalLocale): void {
  setLocale(locale);
}

/** Translation outside components (stores, API helpers). */
export function dpT(key: string, params?: Record<string, unknown>): string {
  return params ? i18n.global.t(key, params) : i18n.global.t(key);
}

/** Translation in components; `localeTag` follows language changes. */
export function useDataPortalI18n() {
  const { t, locale } = useI18n({ useScope: 'global' });
  const localeTag = computed(() => (locale.value === 'en' ? 'en-GB' : 'de-DE'));
  return { t, locale, localeTag, setLocale: setDataPortalLocale };
}
